import { Audio } from "@apis:audio/audio";
import { languageLabel } from "@pages:app/audio/components/language";
import { formatFileSize } from "@utils:file";
import { TFunction } from "i18next";

export const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 100);
  return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}.${ms.toString().padStart(2, "0")}`;
};

/*
 * Wall-clock position in a recording: `mm:ss`, or `h:mm:ss` past one hour.
 */
export const formatClock = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const mmss = `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  return hours > 0 ? `${hours}:${mmss}` : mmss;
};

/*
 * Length of a recording in words: "45 s", "7 min", "1 h 12 min".
 */
export const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total} s`;
  const hours = Math.floor(total / 3600);
  const minutes = Math.round((total % 3600) / 60);
  if (hours === 0) return `${minutes} min`;
  return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
};

/*
 * A file name without its extension, used to name downloads.
 */
export const baseName = (fileName: string): string =>
  fileName.replace(/\.[^.]+$/, "") || fileName;

type TranscriptResult = Audio["GET"]["Response"]["result"] | undefined;
type Segment = Audio["GET"]["Segment"];
type Word = Audio["GET"]["Word"];

/*
 * Consecutive segments of one speaker. `speaker` is the raw backend id,
 * or null when the stage has no speakers.
 */
export interface TranscriptTurn {
  speaker: string | null;
  start: number;
  end: number;
  lines: Segment[];
}

/*
 * Where playback stands in the transcript: turn, line (-1 between lines),
 * and word (null without word timestamps).
 */
export interface PlaybackPosition {
  turn: number;
  line: number;
  word: number | null;
}

export const segmentsOf = (result: TranscriptResult): Segment[] =>
  result?.segments ?? [];

/*
 * The backend emits speakers as strings of digits ("0", "1", ...) but one merge path emits integers,
 * so IDs are always compared as strings and never by truthiness (speaker 0 is a valid ID).
 */
export const speakerOf = (segment: Segment): string | null => {
  if (!("speaker" in segment)) return null;
  const { speaker } = segment;
  return speaker === undefined || speaker === null || speaker === ""
    ? null
    : String(speaker);
};

/*
 * Numbers speakers 1..n in order of first appearance, whatever ids the diarizer gave.
 */
export const renumberSpeakers = (segments: Segment[]): Map<string, number> => {
  const numbers = new Map<string, number>();

  for (const segment of segments) {
    const key = speakerOf(segment);
    if (key !== null && !numbers.has(key)) {
      numbers.set(key, numbers.size + 1);
    }
  }

  return numbers;
};

/*
 * Aligned words carry `word`, diarized words carry `text`; a word's timing can be missing.
 */
export const wordsOf = (segment: Segment): Word[] =>
  "words" in segment && segment.words ? segment.words : [];

export const wordText = (word: Word): string =>
  "word" in word ? word.word : word.text;

/*
 * Merges consecutive segments of one speaker into turns, one line per segment.
 * Without speakers every segment is its own turn.
 */
export const groupIntoTurns = (segments: Segment[]): TranscriptTurn[] => {
  const turns: TranscriptTurn[] = [];

  for (const segment of segments) {
    if (!(segment.text ?? "").trim()) continue;

    const speaker = speakerOf(segment);
    const last = turns[turns.length - 1];

    if (last && speaker !== null && last.speaker === speaker) {
      last.lines.push(segment);
      last.end = segment.end;
    } else {
      turns.push({
        speaker,
        start: segment.start,
        end: segment.end,
        lines: [segment],
      });
    }
  }

  return turns;
};

/*
 * The turn, line and word being spoken at `time`, or null in a silence. Between two timed
 * words the last one started stays current, so the highlight does not flicker.
 */
export const positionAt = (
  turns: TranscriptTurn[],
  time: number,
): PlaybackPosition | null => {
  for (let t = 0; t < turns.length; t++) {
    const turn = turns[t];
    if (time < turn.start) return null;
    if (time > turn.end) continue;

    for (let l = 0; l < turn.lines.length; l++) {
      const line = turn.lines[l];
      if (time < line.start || time > line.end) continue;

      const words = wordsOf(line);
      let word: number | null = null;
      for (let w = words.length - 1; w >= 0; w--) {
        const { start } = words[w];
        if (Number.isFinite(start) && start <= time) {
          word = w;
          break;
        }
      }
      return { turn: t, line: l, word };
    }

    return { turn: t, line: -1, word: null };
  }

  return null;
};

/*
 * The speaker behind each bar of the waveform: the speaker number, 0 for speech
 * without speakers, null for silence.
 */
export const speakerNumbersForBars = (
  turns: TranscriptTurn[],
  numbers: Map<string, number>,
  durationSeconds: number,
  count: number,
): (number | null)[] => {
  const bars: (number | null)[] = new Array(count).fill(null);
  if (!durationSeconds || !count) return bars;

  let t = 0;
  for (let i = 0; i < count; i++) {
    const time = ((i + 0.5) / count) * durationSeconds;
    while (t < turns.length && turns[t].end < time) t++;
    const turn = turns[t];
    if (turn && time >= turn.start && time <= turn.end) {
      bars[i] = turn.speaker !== null ? (numbers.get(turn.speaker) ?? 0) : 0;
    }
  }

  return bars;
};

/*
 * The language the transcription detected, as the backend names it ("French").
 */
export const detectedLanguage = (segments: Segment[]): string | undefined =>
  segments.find((segment) => segment.language)?.language;

/*
 * The result as plain text, one segment per line, with the same speaker labels
 * the page shows. Feeds the text file, the summary and the exports.
 */
export const transcriptToText = (
  result: TranscriptResult,
  formatSpeaker: (n: number) => string,
): string => {
  const segments = segmentsOf(result);
  const numbers = renumberSpeakers(segments);

  return segments
    .map((segment) => {
      const text = (segment.text ?? "").trim();
      if (!text) return "";
      const speaker = speakerOf(segment);
      const number = speaker !== null ? numbers.get(speaker) : undefined;
      return number !== undefined ? `${formatSpeaker(number)}: ${text}` : text;
    })
    .filter(Boolean)
    .join("\n");
};

/*
 * The recording's shape as evenly spaced peaks between 0 and 1, channels mixed by
 * taking the loudest sample of each bucket.
 */
export const computePeaks = (buffer: AudioBuffer, buckets = 160): number[] => {
  const channels = Array.from({ length: buffer.numberOfChannels }, (_, i) =>
    buffer.getChannelData(i),
  );
  const bucketSize = Math.max(1, Math.floor(buffer.length / buckets));
  const peaks: number[] = [];

  for (let bucket = 0; bucket < buckets; bucket++) {
    const start = bucket * bucketSize;
    const end = Math.min(start + bucketSize, buffer.length);
    let peak = 0;
    for (const channel of channels) {
      for (let i = start; i < end; i++) {
        const value = Math.abs(channel[i]);
        if (value > peak) peak = value;
      }
    }
    peaks.push(peak);
  }

  const loudest = Math.max(...peaks, 0.01);
  return peaks.map((peak) => peak / loudest);
};

/*
 * The length of a WAV file the browser cannot decode (compressed encodings such as ADPCM),
 * taken from the frame count in its `fact` chunk. Nothing is guessed from byte rates:
 * an undefined length is better than a wrong one.
 */
export const wavDurationSeconds = (bytes: ArrayBuffer): number | undefined => {
  const view = new DataView(bytes);
  const tag = (offset: number) =>
    String.fromCharCode(
      view.getUint8(offset),
      view.getUint8(offset + 1),
      view.getUint8(offset + 2),
      view.getUint8(offset + 3),
    );
  if (bytes.byteLength < 12 || tag(0) !== "RIFF" || tag(8) !== "WAVE") {
    return undefined;
  }

  let sampleRate: number | undefined;
  let factFrames: number | undefined;
  let offset = 12;
  while (offset + 8 <= bytes.byteLength) {
    const chunk = tag(offset);
    const size = view.getUint32(offset + 4, true);
    if (chunk === "fmt " && offset + 16 <= bytes.byteLength) {
      sampleRate = view.getUint32(offset + 12, true);
    } else if (chunk === "fact" && offset + 12 <= bytes.byteLength) {
      factFrames = view.getUint32(offset + 8, true);
    }
    offset += 8 + size + (size % 2);
  }

  return factFrames && sampleRate ? factFrames / sampleRate : undefined;
};

/*
 * What the user asked for, one line: size and length, then language and speakers if set.
 */
export const requestMeta = (
  request: {
    file: File | null;
    durationSeconds?: number | null;
    language: string;
    numSpeakers: number | null;
  },
  t: TFunction,
): string =>
  [
    request.file ? formatFileSize(request.file.size) : undefined,
    request.durationSeconds
      ? formatDuration(request.durationSeconds)
      : undefined,
    languageLabel(request.language, t),
    request.numSpeakers
      ? t("audio.speakers", { count: request.numSpeakers })
      : undefined,
  ]
    .filter(Boolean)
    .join(", ");
